import { useState, useEffect, useMemo } from 'react';
import { P, cardStyle, inputStyle, labelStyle } from '../styles/theme';
import Btn from './Shared/Btn';
import Badge from './Shared/Badge';
import { exportToCSV } from '../utils/csvExport';
import {
  fetchClients, fetchBookingsForClients,
  insertClient, updateClient, deleteClient,
} from '../services/clientService';

export default function ClientRecords() {
  const [clients,     setClients]     = useState([]);
  const [bookings,    setBookings]    = useState([]);
  const [view,        setView]        = useState('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [clientForm,  setClientForm]  = useState({ name: '', phone: '', email: '', allergies: '', notes: '' });
  const [editingId,   setEditingId]   = useState(null);

  useEffect(() => { loadClients(); loadBookings(); }, []);

  const loadClients  = async () => { try { setClients(await fetchClients()); } catch {} };
  const loadBookings = async () => { setBookings(await fetchBookingsForClients()); };

  const selected       = clients.find(c => c.id === view);
  const getClientBks   = (name, phone) => bookings.filter(b => b.client_name === name && b.client_phone === phone);
  const filteredClients = clients.filter(c => c.name.toLowerCase().includes(searchQuery.toLowerCase()));
  const noShowMap = useMemo(() => {
    const m = {};
    bookings.filter(b => b.status === 'no_show').forEach(b => {
      const k = `${b.client_name}|${b.client_phone}`;
      m[k] = (m[k] || 0) + 1;
    });
    return m;
  }, [bookings]);

  const handleExportClients = () => {
    if (filteredClients.length === 0) return alert('No records!');
    exportToCSV('Client_Database', ['Name','Phone','Email','Allergies','Notes'],
      filteredClients.map(c => [c.name, c.phone, c.email || '', c.allergies, c.notes]));
  };

  const handleSaveClient = async () => {
    if (!clientForm.name.trim()) return alert('Please enter a name!');
    const payload = { name: clientForm.name.trim(), phone: clientForm.phone.trim(), email: clientForm.email.trim(), allergies: clientForm.allergies.trim(), notes: clientForm.notes.trim() };
    try {
      if (editingId) await updateClient(editingId, payload);
      else           await insertClient(payload);
      setClientForm({ name: '', phone: '', email: '', allergies: '', notes: '' });
      setEditingId(null); loadClients(); setView('list');
    } catch (err) { alert('Error saving client: ' + err.message); }
  };

  const handleDeleteClient = async (id) => {
    if (!window.confirm('Delete this client record permanently?')) return;
    try { await deleteClient(id); loadClients(); setView('list'); } catch {}
  };

  const startEditClient = (client) => {
    setEditingId(client.id);
    setClientForm({ name: client.name || '', phone: client.phone || '', email: client.email || '', allergies: client.allergies || '', notes: client.notes || '' });
    setView('newClient');
  };

  return (
    <div>
      {view === 'list' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
            <div>
              <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '26px', color: P.deep, fontWeight: 600 }}>Clients</h2>
              <p style={{ fontSize: '13px', color: P.muted }}>Total {clients.length} registered</p>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <Btn onClick={handleExportClients} variant="secondary" size="sm">📊 Export</Btn>
              <Btn onClick={() => { setEditingId(null); setClientForm({ name: '', phone: '', email: '', allergies: '', notes: '' }); setView('newClient'); }} size="sm">+ New Client</Btn>
            </div>
          </div>
          <input style={{ ...inputStyle, marginBottom: '16px' }} placeholder="🔍 Search by name..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
          {filteredClients.map(c => {
            const cNoShow = noShowMap[`${c.name}|${c.phone}`] || 0;
            return (
              <div key={c.id} style={{ ...cardStyle, cursor: 'pointer' }} onClick={() => setView(c.id)}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <p style={{ fontWeight: 700, color: P.deep }}>{c.name} {c.phone ? `(${c.phone})` : ''}</p>
                    {c.email && <p style={{ fontSize: '12px', color: P.muted }}>{c.email}</p>}
                  </div>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    {cNoShow > 0 && <Badge color={P.red}>🚫 {cNoShow}×</Badge>}
                    {c.allergies && <Badge color={P.red}>⚠️ Allergy</Badge>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {view === 'newClient' && (
        <div style={cardStyle}>
          <h3 style={{ marginBottom: '14px', color: P.deep }}>{editingId ? 'Edit Client' : 'Add New Client'}</h3>
          {[{ label: 'Client Name *', key: 'name', placeholder: 'Name' }, { label: 'Phone Number', key: 'phone', placeholder: 'Phone' }, { label: 'Email', key: 'email', placeholder: 'Email' }, { label: 'Allergies / Special Conditions', key: 'allergies', placeholder: 'e.g., HEMA allergy, acetone sensitivity' }].map(({ label, key, placeholder }) => (
            <div key={key} style={{ marginBottom: '10px' }}>
              <label style={labelStyle}>{label}</label>
              <input style={inputStyle} placeholder={placeholder} value={clientForm[key]} onChange={e => setClientForm({ ...clientForm, [key]: e.target.value })} />
            </div>
          ))}
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>General Notes</label>
            <textarea style={{ ...inputStyle, height: '80px', resize: 'none' }} placeholder="Prefers square shape..." value={clientForm.notes} onChange={e => setClientForm({ ...clientForm, notes: e.target.value })} />
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Btn onClick={handleSaveClient} style={{ flex: 1 }}>{editingId ? 'Save Changes' : 'Save Client'}</Btn>
            <Btn onClick={() => setView('list')} variant="ghost">Cancel</Btn>
          </div>
        </div>
      )}
      {view !== 'list' && view !== 'newClient' && selected && (() => {
        const clientBks     = getClientBks(selected.name, selected.phone);
        const completedBks  = clientBks.filter(b => b.status === 'completed');
        const totalVisits   = completedBks.length;
        const lifetimeSpend = completedBks.reduce((sum, b) => sum + Number(b.total_amount || 0), 0);
        const avgSpend      = totalVisits > 0 ? lifetimeSpend / totalVisits : 0;
        const clientNoShows = clientBks.filter(b => b.status === 'no_show').length;
        const isVIP         = totalVisits >= 10 || lifetimeSpend >= 1000;
        return (
          <div>
            <Btn variant="ghost" onClick={() => setView('list')} size="sm">← Back to List</Btn>
            <div style={{ ...cardStyle, marginTop: '14px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '4px' }}>
                    <h3 style={{ fontSize: '22px', color: P.deep, margin: 0 }}>{selected.name}</h3>
                    {isVIP && <Badge color={P.amber}>⭐ VIP</Badge>}
                    {clientNoShows > 0 && <Badge color={P.red}>🚫 {clientNoShows} No-Show</Badge>}
                  </div>
                  {selected.phone && <p style={{ color: P.muted, marginTop: '2px' }}>📞 {selected.phone}</p>}
                  {selected.email && <p style={{ color: P.muted, marginTop: '2px' }}>✉️ {selected.email}</p>}
                </div>
                <div style={{ display: 'flex', gap: '6px' }}>
                  <Btn size="sm" variant="secondary" onClick={() => startEditClient(selected)}>Edit</Btn>
                  <button onClick={() => handleDeleteClient(selected.id)} style={{ background: 'none', border: 'none', color: P.red, cursor: 'pointer', fontSize: '13px' }}>Delete</button>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px', margin: '12px 0' }}>
                {[
                  { label: 'Total Visits',   value: totalVisits,                    color: P.deep  },
                  { label: 'Lifetime Spend', value: `$${lifetimeSpend.toFixed(0)}`, color: P.green },
                  { label: 'Avg per Visit',  value: `$${avgSpend.toFixed(0)}`,      color: P.rose  },
                  { label: 'No-Shows',       value: clientNoShows,                  color: clientNoShows > 0 ? P.red : P.green },
                ].map(s => (
                  <div key={s.label} style={{ textAlign: 'center', padding: '10px 6px', background: P.bg, borderRadius: '10px' }}>
                    <p style={{ fontSize: '22px', fontWeight: 800, color: s.color, margin: '0 0 2px' }}>{s.value}</p>
                    <p style={{ fontSize: '10px', fontWeight: 700, color: P.muted, textTransform: 'uppercase', letterSpacing: '0.05em', margin: 0 }}>{s.label}</p>
                  </div>
                ))}
              </div>
              <div style={{ borderTop: `1px solid ${P.border}`, paddingTop: '12px', marginTop: '0' }}>
                <label style={labelStyle}>⚠️ Allergies & Alerts</label>
                <p style={{ color: selected.allergies ? P.red : P.text, fontSize: '14px', fontWeight: selected.allergies ? 600 : 400, marginBottom: '14px' }}>{selected.allergies || 'No allergies reported.'}</p>
                <label style={labelStyle}>📝 Professional Notes</label>
                <p style={{ color: P.text, fontSize: '14px', whiteSpace: 'pre-line', marginBottom: '14px' }}>{selected.notes || 'No notes yet.'}</p>
              </div>
            </div>
            <div style={cardStyle}>
              <h4 style={{ color: P.deep, marginBottom: '12px', fontWeight: 700 }}>📅 Booking History ({clientBks.length})</h4>
              {clientBks.length === 0 ? (
                <p style={{ color: P.muted, fontSize: '13px' }}>No bookings found for this client.</p>
              ) : clientBks.map(b => (
                <div key={b.id} style={{ padding: '10px 0', borderBottom: `1px solid ${P.border}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <p style={{ fontWeight: 700, fontSize: '13px', color: P.deep }}>{new Date(b.booking_date).toLocaleString('en-AU')}</p>
                      {b.service_name && <p style={{ fontSize: '12px', color: P.text }}>{b.service_name}</p>}
                      <p style={{ fontSize: '12px', color: P.muted }}>Total: ${b.total_amount} | Deposit: ${b.deposit_amount}</p>
                    </div>
                    <Badge color={{ confirmed: P.green, completed: P.deep, cancelled: P.red, no_show: '#B56060', pending: P.amber }[b.status] || P.amber}>
                      {b.status.replace('_', ' ').toUpperCase()}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })()}
    </div>
  );
}
